import {
  clamp,
  formatKm,
  getJourneyCamera,
  getLapDetails,
  CANADA_ROUTE_KM,
  milestones,
  routeSegments,
} from "./journeyModel";

const spaceDecorations = [
  { id: "space-01", x: 706, y: 1264, size: 150, rotate: 11, opacity: 0.5 },
  { id: "space-02", x: 802, y: 514, size: 92, rotate: 18, opacity: 0.66 },
  { id: "space-03", x: 178, y: 690, size: 72, rotate: 9, opacity: 0.54 },
  { id: "space-04", x: 720, y: 780, size: 126, rotate: -10, opacity: 0.68 },
  { id: "space-05", x: 96, y: 960, size: 136, rotate: 14, opacity: 0.6 },
  { id: "space-06", x: 836, y: 1060, size: 84, rotate: -20, opacity: 0.55 },
  { id: "space-07", x: 270, y: 1166, size: 96, rotate: -8, opacity: 0.62 },
  { id: "space-08", x: 104, y: 400, size: 104, rotate: -16, opacity: 0.74 },
  { id: "space-09", x: 126, y: 1340, size: 118, rotate: 22, opacity: 0.48 },
];

function getAssetUrl(fileName) {
  return `${process.env.PUBLIC_URL}/img/big-canada-run/${fileName}`;
}

function getWebpFileName(fileName) {
  return fileName.replace(/\.png$/, ".webp");
}

function PictureAsset({ alt = "", className, fileName, imgClassName, style }) {
  return (
    <picture className={className} style={style}>
      <source srcSet={getAssetUrl(getWebpFileName(fileName))} type="image/webp" />
      <img className={imgClassName} src={getAssetUrl(fileName)} alt={alt} />
    </picture>
  );
}

function getSeasonalCanadaAvatarFile(date = new Date()) {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if ((month === 6 && day >= 21) || month === 7 || month === 8 || (month === 9 && day <= 21)) {
    return "01-Avatar-Summer.png";
  }

  if ((month === 9 && day >= 22) || month === 10 || month === 11 || (month === 12 && day <= 20)) {
    return "02-Avatar-Fall.png";
  }

  if ((month === 12 && day >= 21) || month === 1 || month === 2 || (month === 3 && day <= 20)) {
    return "03-Avatar-Winter.png";
  }

  return "04-Avatar-Spring.png";
}

function getAvatarFile(distanceInLap) {
  if (distanceInLap > CANADA_ROUTE_KM) {
    return "05-Avatar-Space.png";
  }

  return getSeasonalCanadaAvatarFile();
}

function getSegmentProgress(distanceInLap, segment) {
  const rawProgress = (distanceInLap - segment.startKm) / (segment.endKm - segment.startKm || 1);
  const progress = clamp(rawProgress, 0, 1);

  return progress * progress * (3 - 2 * progress);
}

export default function SpaceWorld({
  calibrationEnabled,
  calibrationPlacementEnabled,
  calibrationPoints,
  camera,
  distanceKm,
  onCalibrationClick,
  worldStyle,
}) {
  const { distanceInLap } = getLapDetails(distanceKm);
  const nextMilestoneIndex = milestones.findIndex((milestone) => milestone.km > distanceInLap);
  const previousMilestoneIndex =
    nextMilestoneIndex === -1 ? milestones.length - 1 : Math.max(nextMilestoneIndex - 1, 0);
  const routeSegmentStates = routeSegments.map((segment) => ({
    ...segment,
    progress: getSegmentProgress(distanceInLap, segment),
  }));
  const avatarWorldStyle = {
    left: camera.x,
    top: camera.y,
  };
  const avatarFile = getAvatarFile(distanceInLap);

  const handleCalibrationClick = (event) => {
    if (!calibrationEnabled || !calibrationPlacementEnabled || !onCalibrationClick) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 1040;
    const y = ((event.clientY - rect.top) / rect.height) * 2020;

    onCalibrationClick({
      x: Math.round(x),
      y: Math.round(y),
    });
  };

  return (
    <div className="bcr-world" style={worldStyle}>
      <div className="bcr-stars bcr-stars-one" />
      <div className="bcr-stars bcr-stars-two" />
      <div className="bcr-earth-glow" />
      <div className="bcr-earth">
        <PictureAsset fileName="earth-1.png" />
      </div>
      <div className="bcr-atmosphere bcr-atmosphere-one" />
      <div className="bcr-atmosphere bcr-atmosphere-two" />
      <div className="bcr-moon">
        <PictureAsset fileName="moon.png" />
      </div>
      <div className="bcr-moon-orbit" />
      <div className="bcr-space-decoration-layer" aria-hidden="true">
        {spaceDecorations.map((decoration) => (
          <PictureAsset
            key={decoration.id}
            className="bcr-space-decoration"
            fileName={`${decoration.id}.png`}
            style={{
              left: decoration.x,
              top: decoration.y,
              width: decoration.size,
              height: decoration.size,
              opacity: decoration.opacity,
              transform: `translate(-50%, -50%) rotate(${decoration.rotate}deg)`,
            }}
          />
        ))}
      </div>
      <svg className="bcr-route-svg" viewBox="0 0 1040 2020" aria-hidden="true">
        <defs>
          {routeSegmentStates
            .filter((segment) => segment.progress > 0)
            .map((segment) => {
              const progress = segment.progress * 100;

              return (
                <mask
                  key={`${segment.id}-completed-mask`}
                  id={`bcr-${segment.id}-completed-mask`}
                  maskUnits="userSpaceOnUse"
                >
                  <path
                    className="bcr-route-progress-mask"
                    d={segment.d}
                    pathLength="100"
                    style={{ strokeDasharray: `${progress} 100` }}
                  />
                </mask>
              );
            })}
        </defs>
        {routeSegmentStates.map((segment) => (
          <path key={`${segment.id}-shadow`} className="bcr-route-shadow" d={segment.d} />
        ))}
        {routeSegmentStates.map((segment) => (
          <path key={`${segment.id}-line`} className={segment.lineClassName} d={segment.d} />
        ))}
        {routeSegmentStates
          .filter((segment) => segment.progress > 0)
          .map((segment) => (
            <path
              key={`${segment.id}-completed-line`}
              className={`${segment.lineClassName} bcr-route-line-completed`}
              d={segment.d}
              mask={`url(#bcr-${segment.id}-completed-mask)`}
            />
          ))}
      </svg>
      {calibrationEnabled && (
        <div
          className={`bcr-route-calibration-layer ${
            calibrationPlacementEnabled ? "bcr-route-calibration-layer-active" : ""
          }`}
          onClick={handleCalibrationClick}
          onPointerDown={(event) => {
            if (calibrationPlacementEnabled) {
              event.stopPropagation();
            }
          }}
          role="presentation"
        >
          {calibrationPoints.map((point) => (
            <span
              key={point.id}
              className="bcr-route-calibration-dot"
              style={{
                left: point.x,
                top: point.y,
              }}
            >
              {point.id}
            </span>
          ))}
        </div>
      )}
      {milestones.map((milestone, index) => {
        const marker = getJourneyCamera(milestone.km);
        const isPreviousMilestone = index === previousMilestoneIndex;
        const isNextMilestone = index === nextMilestoneIndex;
        const isActiveMilestone = isPreviousMilestone || isNextMilestone;
        const labelOpacity = isActiveMilestone ? 1 : 0;
        const markerOpacity = isActiveMilestone ? 1 : 0.24;

        return (
          <div
            key={`${milestone.km}-${milestone.label}`}
            className="bcr-mile-marker"
            style={{
              left: marker.x,
              top: marker.y,
              opacity: markerOpacity,
              "--label-opacity": labelOpacity,
            }}
          >
            <span className="bcr-mile-marker-label">
              <strong>{formatKm(milestone.km)} km</strong>
              <em>{milestone.label}</em>
            </span>
          </div>
        );
      })}
      <div className="bcr-world-avatar" style={avatarWorldStyle} aria-hidden="true">
        <PictureAsset fileName={avatarFile} />
      </div>
    </div>
  );
}
